import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Button, Flex, Group, Paper, Text, Title } from '@mantine/core';
import { IconAlertCircle, IconHome, IconRefresh } from '@tabler/icons-react';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (process.env.NODE_ENV !== 'production') {
      console.error('Uncaught error caught by ErrorBoundary:', error, errorInfo);
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Flex
          direction="column"
          align="center"
          justify="center"
          mih="80vh"
          w="100%"
          px="md"
          py="xl"
        >
          <Paper
            p="xl"
            radius="lg"
            bg="#1c1c1c"
            style={{
              border: '1px solid #333',
              maxWidth: 540,
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Flex justify="center" mb="md">
              <Box
                p="md"
                style={{
                  borderRadius: '50%',
                  backgroundColor: 'rgba(250, 82, 82, 0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <IconAlertCircle size={40} color="#fa5252" />
              </Box>
            </Flex>

            <Title order={2} fz="h3" fw={700} c="white" mb="xs">
              Something went wrong
            </Title>

            <Text c="dimmed" fz="sm" mb="xl">
              An unexpected error occurred while displaying this page. You can try refreshing the page or
              returning to the home dashboard.
            </Text>

            <Group justify="center" gap="sm">
              <Button
                variant="filled"
                color="orange"
                radius="xl"
                leftSection={<IconRefresh size={16} />}
                onClick={this.handleReload}
              >
                Reload Page
              </Button>
              <Button
                variant="default"
                radius="xl"
                leftSection={<IconHome size={16} />}
                onClick={this.handleGoHome}
              >
                Return to Home
              </Button>
            </Group>
          </Paper>
        </Flex>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
